import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, LanguageSwitch, Logo } from '@/components/common';
import { errorMessage } from '@/lib/api-client';
import { navigate, parseRoute } from '@/lib/router';
import { isInstalledApp } from '@/utils/browser';
import { useAuth } from './AuthContext';

export function LoginPage() {
  const { t } = useTranslation();
  const { login, expired, changeLanguage } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      // From /login go to the patient list; after an expired session stay where you were.
      if (parseRoute(window.location.pathname).name === 'login')
        navigate({ name: 'patients' }, true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__brand">
          <h1 className="grow">
            <Logo size={44} />
          </h1>
          <div className="login__lang">
            <LanguageSwitch onChange={changeLanguage} />
          </div>
        </div>
        {expired && !error && <Banner tone="warning">{t('login.expired')}</Banner>}
        {error !== null && (
          <Banner tone="error">
            {/* On the login form a 401 means wrong credentials, not an expired session. */}
            {errorMessage(error, { UNAUTHORIZED: t('login.invalidCredentials') })}
          </Banner>
        )}
        <div className="field">
          <label htmlFor="email">{t('login.email')}</label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">{t('login.password')}</label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? t('login.submitting') : t('login.submit')}
        </button>
        {!isInstalledApp() && (
          <a className="login__home small" href="/">
            {t('login.home')}
          </a>
        )}
      </form>
    </div>
  );
}
